import Box from "@mui/material/Box";
import { useMemo, useState } from "react";

import type { TimeCheckSuggestion, TournamentSetupView } from "@/libs/tournamentSetupApi";

import { formatDay } from "../setup/fields";
import { GlossaryProvider } from "../setup/glossary-drawer";
import { TimeCheckBar } from "../setup/time-check-bar";
import { DenseButton, DenseInput, DenseSelect, FieldLabel, Note, STAFF_FONT, tone } from "../setup/ui";
import { AddEventsDialog } from "./add-events-dialog";
import { BulkEditPanel } from "./bulk-edit-panel";
import { EventMoreOptions } from "./event-more-options";
import { EventPanel } from "./event-panel";
import { EventsTable } from "./events-table";
import type { GroupBy } from "./events-table";
import { useSetupCommand } from "./use-setup-command";

type Filters = { day: string; entry: string; format: string; division: string };
const ANY: Filters = { day: "", entry: "", format: "", division: "" };

function FilterChip({ label, value, options, onChange }: { label: string; value: string; options: { value: string; label: string }[]; onChange: (value: string) => void }) {
  return (
    <Box
      aria-label={`${label} filter`}
      component="select"
      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
      sx={{
        height: 28,
        px: "10px",
        borderRadius: "14px",
        border: `1px solid ${value ? tone.brandHi : tone.line}`,
        bgcolor: value ? tone.brandSoft : tone.surface,
        font: "inherit", fontWeight: 600, fontSize: 12,
        color: value ? tone.brandText : tone.ink2,
        cursor: "pointer",
      }}
      value={value}
    >
      <option value="">{label}: any</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {label}: {option.label}
        </option>
      ))}
    </Box>
  );
}

/** The Events tab (F2, F7, F8): grouped table with filters and multi-select,
 * the time check with ranked fixes, and the one-event panel or bulk edit. */
export function EventsWorkspace({
  view,
  onViewChange,
  onEditTime,
  onOpenOverview,
}: {
  view: TournamentSetupView;
  onViewChange: (next: TournamentSetupView) => void;
  onEditTime: () => void;
  onOpenOverview: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [opened, setOpened] = useState<string | null>(view.events[0]?.event_id ?? null);
  const [groupBy, setGroupBy] = useState<GroupBy>("day");
  const [query, setQuery] = useState("");
  const [filters, setFilters] = useState<Filters>(ANY);
  const [showFilters, setShowFilters] = useState(false);
  const [moreOptions, setMoreOptions] = useState(false);
  const [adding, setAdding] = useState(false);
  const { pending, error, run } = useSetupCommand(view, onViewChange);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return view.events.filter(
      (event) =>
        (!needle || event.name.toLowerCase().includes(needle)) &&
        (!filters.day || event.stage_days.some((s) => s.play_date === filters.day)) &&
        (!filters.entry || event.entry_unit === filters.entry) &&
        (!filters.format || event.format === filters.format) &&
        (!filters.division || event.division_label === filters.division),
    );
  }, [view.events, query, filters]);
  const chosen = view.events.filter((event) => selected.has(event.event_id));
  const bulk = chosen.length > 1;
  const single = bulk ? null : (chosen[0] ?? view.events.find((event) => event.event_id === opened) ?? null);

  const applySuggestion = (suggestion: TimeCheckSuggestion) =>
    void run({ command: "apply_changes", changes: suggestion.apply } as never);

  if (moreOptions && single) {
    return (
      <GlossaryProvider>
        <EventMoreOptions
          event={single}
          onClose={() => setMoreOptions(false)}
          onOpenOverview={onOpenOverview}
          onSelectEvent={(id) => {
            setSelected(new Set());
            setOpened(id);
          }}
          onViewChange={onViewChange}
          view={view}
        />
      </GlossaryProvider>
    );
  }

  const formats = [...new Map(view.events.map((e) => [e.format, e.format_label])).entries()];
  const divisions = [...new Set(view.events.map((e) => e.division_label))];
  return (
    <GlossaryProvider>
      {/* Below 900 px the event panel and bulk edit stack under the table. */}
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: { xs: "stretch", md: "flex-start" }, minWidth: 0 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: "100%", bgcolor: tone.surface, border: `1px solid ${tone.line}`, borderRadius: "10px", display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ minHeight: 44, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, px: "14px", py: { xs: 1, md: 0 }, borderBottom: `1px solid ${tone.divider}`, fontWeight: 700, fontSize: 13.5 }}>
            <span>
              {view.events.length} event{view.events.length === 1 ? "" : "s"}
            </span>
            <DenseInput aria-label="Search events" onChange={(e) => setQuery(e.target.value)} placeholder="Search events" sx={{ width: { xs: 150, sm: 200 }, ml: "6px", height: 32, fontSize: 13 }} value={query} />
            <DenseButton aria-expanded={showFilters} onClick={() => setShowFilters(!showFilters)} size="sm">
              Filter ▾
            </DenseButton>
            <FieldLabel htmlFor="grp" sx={{ m: "0 0 0 4px" }}>
              Group by
            </FieldLabel>
            <DenseSelect id="grp" onChange={(e) => setGroupBy(e.target.value as GroupBy)} sx={{ width: 120, height: 32, fontSize: 13 }} value={groupBy}>
              <option value="day">Day</option>
              <option value="discipline">Discipline</option>
              <option value="division">Division</option>
              <option value="none">None</option>
            </DenseSelect>
            <Box sx={{ flexGrow: 1 }} />
            <DenseButton disabled={!view.allowed_actions.includes("add_events")} onClick={() => setAdding(true)} size="sm">
              + Add events
            </DenseButton>
          </Box>
          {showFilters || bulk ? (
            <Box sx={{ display: "flex", gap: "6px", alignItems: "center", p: "8px 14px", borderBottom: `1px solid ${tone.divider}`, flexWrap: "wrap" }}>
              {showFilters ? (
                <>
                  <Box component="span" sx={{ fontSize: 12, fontWeight: 600, color: tone.muted }}>
                    Filter
                  </Box>
                  <FilterChip label="Day" onChange={(v) => setFilters({ ...filters, day: v })} options={view.days.map((d) => ({ value: d.play_date, label: formatDay(d.play_date) }))} value={filters.day} />
                  <FilterChip label="Entry type" onChange={(v) => setFilters({ ...filters, entry: v })} options={[{ value: "players", label: "Players" }, { value: "pairs", label: "Pairs" }]} value={filters.entry} />
                  <FilterChip label="Format" onChange={(v) => setFilters({ ...filters, format: v })} options={formats.map(([value, label]) => ({ value, label }))} value={filters.format} />
                  <FilterChip label="Division" onChange={(v) => setFilters({ ...filters, division: v })} options={divisions.map((d) => ({ value: d, label: d }))} value={filters.division} />
                </>
              ) : null}
              <Box sx={{ flexGrow: 1 }} />
              {bulk ? (
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1, height: 30, px: "10px", borderRadius: "6px", bgcolor: tone.brandSoft, border: `1px solid ${tone.brandHi}`, fontSize: 12.5, color: tone.brandNoteText, fontWeight: 600 }}>
                  {chosen.length} selected
                  <DenseButton onClick={() => setMoreOptions(false)} size="sm" sx={{ height: 24 }}>
                    Edit {chosen.length} selected
                  </DenseButton>
                  <DenseButton onClick={() => setSelected(new Set())} size="sm" sx={{ height: 24 }}>
                    Clear
                  </DenseButton>
                </Box>
              ) : null}
            </Box>
          ) : null}
          {view.time_check.days.length && !view.time_check.all_fit ? (
            <TimeCheckBar onApply={applySuggestion} onEditAssumptions={onEditTime} pending={pending} result={view.time_check} />
          ) : null}
          {error ? (
            <Note role="alert" sx={{ m: "8px 14px" }} tone="red">
              {error}
            </Note>
          ) : null}
          <EventsTable
            columns={bulk ? "bulk" : single ? "panel" : "full"}
            events={visible}
            groupBy={groupBy}
            onOpen={(id) => {
              setSelected(new Set());
              setOpened(id);
            }}
            onSelect={(ids) => setSelected(ids)}
            opened={single?.event_id ?? null}
            result={view.time_check}
            selected={selected}
          />
          {view.events.length > 20 || bulk ? (
            <Box sx={{ mt: "auto", p: "8px 14px", borderTop: `1px solid ${tone.divider}`, fontSize: 12, color: tone.subtle }}>
              Built for 40+ events: groups collapse to a count, filters and search stay on top, and shift-click selects a range.
            </Box>
          ) : null}
        </Box>
        {single ? (
          <Box sx={{ width: { xs: "100%", md: 600 }, flex: "none", position: { xs: "static", md: "sticky" }, top: 16 }}>
            <EventPanel event={single} onMoreOptions={() => setMoreOptions(true)} onViewChange={onViewChange} view={view} />
          </Box>
        ) : null}
        {bulk ? <BulkEditPanel events={chosen} onDone={() => setSelected(new Set())} onViewChange={onViewChange} view={view} /> : null}
      </Box>
      <AddEventsDialog onClose={() => setAdding(false)} onViewChange={onViewChange} open={adding} view={view} />
    </GlossaryProvider>
  );
}
